import { MODULES, type ModuleId } from "@gosaas/modules";

export type { ModuleId };

export type PublicAPIClientLang = keyof typeof MODULES.public_api.clients;

type ScalarModuleId = Exclude<
	ModuleId,
	"auth" | "operations" | "billing" | "public_api"
>;

/** Whether `modules.yaml` has the module on (missing key defaults true). */
export function isModuleEnabled(id: ModuleId): boolean {
	if (id === "auth") {
		return MODULES.auth.enabled !== false;
	}
	if (id === "operations") {
		return MODULES.operations.enabled !== false;
	}
	if (id === "billing") {
		return MODULES.billing.enabled !== false;
	}
	if (id === "public_api") {
		return MODULES.public_api.enabled !== false;
	}
	const v = MODULES[id as ScalarModuleId];
	return v !== false;
}

/** OAuth routes and UI (auth.oauth; omitted defaults to on when auth is on). */
export function isAuthOAuthEnabled(): boolean {
	return isModuleEnabled("auth") && MODULES.auth.oauth.enabled;
}

export function isAuthOAuthGoogleEnabled(): boolean {
	return isAuthOAuthEnabled() && MODULES.auth.oauth.google;
}

export function isAuthOAuthGitHubEnabled(): boolean {
	return isAuthOAuthEnabled() && MODULES.auth.oauth.github;
}

/** Staff admin console (auth.staff; omitted defaults to on when auth is on). */
export function isStaffConsoleEnabled(): boolean {
	return isModuleEnabled("auth") && MODULES.auth.staff;
}

export function isOperationsLegalEnabled(): boolean {
	return isModuleEnabled("operations") && MODULES.operations.legal;
}

export function isOperationsSecurityEnabled(): boolean {
	return isModuleEnabled("operations") && MODULES.operations.security;
}

export function isLegalDocumentModuleEnabled(
	module: "legal" | "security",
): boolean {
	return module === "security"
		? isOperationsSecurityEnabled()
		: isOperationsLegalEnabled();
}

/** Integrator /api/v1 surface and API reference docs (team routes are gated separately). */
export function isPublicApiSurfaceEnabled(): boolean {
	return isModuleEnabled("public_api");
}

/** Public help center at /docs. */
export function isDocsSurfaceEnabled(): boolean {
	return isModuleEnabled("docs");
}

/** Short reference articles at /docs/knowledge-base. */
export function isKnowledgeBaseSurfaceEnabled(): boolean {
	return isModuleEnabled("knowledge_base");
}

/** MCP server package and MCP developer docs (requires public_api). */
export function isMcpSurfaceEnabled(): boolean {
	return isPublicApiSurfaceEnabled() && MODULES.public_api.mcp;
}

/** Whether a publishable integrator client language is enabled under public_api.clients. */
export function isPublicClientEnabled(lang: PublicAPIClientLang): boolean {
	return isPublicApiSurfaceEnabled() && MODULES.public_api.clients[lang];
}

/** Permanent Free tier in modules.yaml (off = paid-default onboarding). */
export function hasBillingFreeTier(): boolean {
	return isModuleEnabled("billing") && MODULES.billing.free_tier;
}

export function isBillingFreeTrialEnabled(): boolean {
	return isModuleEnabled("billing") && MODULES.billing.free_trial.enabled;
}

export function billingFreeTrialDays(): number {
	return MODULES.billing.free_trial.days;
}

/** Plan-based API quotas, rate limits, and query add-on packs (billing.api_pricing). */
export function isBillingApiPricingEnabled(): boolean {
	return isModuleEnabled("billing") && MODULES.billing.api_pricing;
}
